import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, euro, dataOraIt, STATI_NOLEGGIO } from "@/lib/api";
import { PrintHeader, PrintFooter } from "@/components/PrintHeader";
import { FirmaCliente } from "@/components/FirmaCliente";
import { CondividiButton } from "@/components/CondividiButton";
import { UNITA_LABEL } from "@/components/NoleggioDialog";

const Th = ({ children, right }) => <th className={`border-b-2 border-slate-900 py-1.5 text-[10px] font-bold uppercase tracking-wider ${right ? "text-right" : "text-left"}`}>{children}</th>;
const Td = ({ children, right, mono }) => <td className={`border-b border-slate-200 py-1.5 ${right ? "text-right" : ""} ${mono ? "font-mono" : ""}`}>{children}</td>;
const Box = ({ title, children }) => (
  <div className="rounded border border-slate-300 p-3"><p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{title}</p>{children}</div>
);

export default function ContrattoNoleggio({ pubblico }) {
  const params = useParams();
  const id = pubblico?.doc.id || params.id;
  const navigate = useNavigate();
  const [n, setN] = useState(pubblico?.doc || null);
  const [c, setC] = useState(pubblico?.cliente || {});
  const [bici, setBici] = useState(pubblico?.bici || []);
  const [neg, setNeg] = useState(pubblico?.neg || {});

  const loadN = () => api.get(`/noleggi/${id}`).then((r) => setN(r.data));
  useEffect(() => {
    if (pubblico) return;
    api.get(`/noleggi/${id}`).then((r) => {
      setN(r.data);
      api.get(`/clienti/${r.data.cliente_id}`).then((x) => setC(x.data)).catch(() => {});
    });
    api.get("/bici_noleggio").then((r) => setBici(r.data));
    api.get("/impostazioni").then((r) => setNeg(r.data));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!n) return <div className="py-20 text-center text-sm text-slate-500">Caricamento...</div>;
  const righe = bici.filter((b) => n.bici_ids.includes(b.id));
  const tariffa = (b) => b[`tariffa_${n.tariffa_tipo}`] || 0;
  const unitaLabel = UNITA_LABEL[n.tariffa_tipo]?.[n.unita === 1 ? 0 : 1] || "";

  return (
    <div className="min-h-screen bg-slate-100 py-8 print:bg-white print:py-0">
      <div className="mx-auto mb-4 flex max-w-[210mm] flex-wrap justify-between gap-2 px-4 print:hidden">
        {!pubblico && <Button variant="outline" onClick={() => navigate("/noleggi")} data-testid="contratto-back-button"><ArrowLeft className="mr-1 h-4 w-4" /> Torna ai noleggi</Button>}
        <div className="ml-auto flex gap-2">
          {!pubblico && <CondividiButton tipo="contratto" id={id} telefono={c.telefono || n.cliente_telefono} nome={c.nome || n.cliente_nome} numero={n.numero} firmato={!!n.firma_cliente} negozio={neg.nome_negozio} />}
          <Button onClick={() => window.print()} className="bg-orange-600 hover:bg-orange-700" data-testid="contratto-print-button"><Printer className="mr-1 h-4 w-4" /> {pubblico ? "Stampa / Salva PDF" : "Stampa"}</Button>
        </div>
      </div>
      <div className="print-sheet mx-auto max-w-[210mm] bg-white p-10 text-[12px] text-slate-900 shadow-md print:max-w-none print:p-0 print:shadow-none" data-testid="contratto-sheet">
        <PrintHeader neg={neg} titolo={neg.titolo_contratto || "Contratto di noleggio"}>
          <p className="font-mono text-2xl font-bold" data-testid="contratto-numero">{n.numero}</p>
          <p>Stato: <b>{STATI_NOLEGGIO.find((x) => x.value === n.stato)?.label}</b></p>
        </PrintHeader>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <Box title="Cliente (locatario)">
            <p className="font-bold" data-testid="contratto-cliente">{c.nome} {c.cognome}</p>
            <p>{c.indirizzo}</p><p>{[c.telefono, c.email].filter(Boolean).join(" · ")}</p>
            <p className="mt-1">Documento: <b>{n.documento || "______________________"}</b></p>
          </Box>
          <Box title="Periodo di noleggio">
            <p>Ritiro: <b data-testid="contratto-inizio">{dataOraIt(n.inizio)}</b></p>
            <p>Riconsegna prevista: <b data-testid="contratto-fine">{dataOraIt(n.fine)}</b></p>
            <p>Tariffa: <b>{{ ora: "oraria", mezza: "mezza giornata", giorno: "giornaliera", weekend: "weekend (sabato–domenica)" }[n.tariffa_tipo]}</b> · {n.unita} {unitaLabel}</p>
          </Box>
        </div>
        <table className="mt-5 w-full">
          <thead><tr><Th>Codice</Th><Th>Bici</Th><Th>Taglia</Th><Th>N° telaio</Th><Th right>Tariffa</Th><Th right>Importo</Th></tr></thead>
          <tbody>
            {righe.map((b) => (
              <tr key={b.id}><Td mono>{b.codice}</Td><Td>{b.marca} {b.modello}</Td><Td>{b.taglia}</Td><Td mono>{b.telaio || "—"}</Td><Td right mono>{euro(tariffa(b))}</Td><Td right mono>{euro(tariffa(b) * n.unita)}</Td></tr>
            ))}
          </tbody>
        </table>
        <div className="ml-auto mt-5 w-72 text-[13px]">
          <div className="flex justify-between border-t-2 border-slate-900 py-2 text-base font-bold"><span>TOTALE NOLEGGIO</span><span className="font-mono" data-testid="contratto-totale">{euro(n.totale)}</span></div>
          <div className="flex justify-between py-1"><span>Cauzione versata</span><span className="font-mono font-bold" data-testid="contratto-cauzione">{euro(n.cauzione)}</span></div>
        </div>
        {n.note && <div className="mt-3"><Box title="Note"><p className="whitespace-pre-wrap">{n.note}</p></Box></div>}
        <div className="mt-5" data-testid="contratto-condizioni">
          <p className="mb-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">Condizioni di noleggio</p>
          <p className="whitespace-pre-wrap text-[11px] leading-relaxed text-slate-700">{neg.condizioni_noleggio}</p>
        </div>
        <p className="mt-3 text-[10px] text-slate-500" data-testid="contratto-privacy-note">Il cliente dichiara di aver ricevuto l'informativa sul trattamento dei dati personali ai sensi del Reg. UE 2016/679 (GDPR) e autorizza il trattamento dei dati per l'esecuzione del contratto.</p>
        <p className="mt-4 text-[11px] text-slate-600">Data: ____ / ____ / ________</p>
        <div className="mt-6 grid grid-cols-2 items-end gap-16 text-[11px] text-slate-500">
          <div className="border-t border-slate-400 pt-1">Firma del negozio</div>
          <FirmaCliente label="Firma del cliente per accettazione delle condizioni" endpoint={`/noleggi/${id}/firma`} doc={n} onSaved={loadN} prefix="contratto" readOnly={!!pubblico} />
        </div>
        <div className="mt-10 rounded border border-dashed border-slate-300 p-3 text-[11px] text-slate-600">
          <p className="mb-6 font-bold uppercase tracking-wider text-slate-500">Riconsegna</p>
          <div className="grid grid-cols-3 gap-6">
            <div className="border-t border-slate-400 pt-1">Data e ora riconsegna</div>
            <div className="border-t border-slate-400 pt-1">Stato bici / danni</div>
            <div className="border-t border-slate-400 pt-1">Firma per restituzione cauzione</div>
          </div>
        </div>
        <PrintFooter text={neg.piede_contratto} testId="contratto-piede" />
      </div>
    </div>
  );
}
